import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

export type MatchSource = "all" | "exhibition" | "pop_balloon" | "global_connect";

export interface UnifiedMatch {
  id: string;
  user1_id: string;
  user2_id: string;
  created_at: string;
  match_source: string;
  met_context: string | null;
  global_connect_country: string | null;
  is_global_match: boolean | null;
  arena_id: string | null;
  source_context: Record<string, unknown> | null;
  matchedProfile: {
    id: string;
    name: string;
    age: number | null;
    photo_url: string | null;
    is_verified: boolean | null;
  } | null;
}

export const useUnifiedMatches = () => {
  const { user } = useAuthContext();
  const [matches, setMatches] = useState<UnifiedMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [sourceFilter, setSourceFilter] = useState<MatchSource>("all");

  const fetchMatches = useCallback(async () => {
    if (!user) {
      setMatches([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("matches")
        .select(`
          id,
          user1_id,
          user2_id,
          created_at,
          match_source,
          met_context,
          global_connect_country,
          is_global_match,
          arena_id,
          source_context,
          user1:profiles!matches_user1_id_fkey(id, name, age, photo_url, is_verified),
          user2:profiles!matches_user2_id_fkey(id, name, age, photo_url, is_verified)
        `)
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      if (data) {
        const transformedMatches: UnifiedMatch[] = data.map((match: any) => ({
          id: match.id,
          user1_id: match.user1_id,
          user2_id: match.user2_id,
          created_at: match.created_at,
          match_source: match.match_source || "pop_balloon",
          met_context: match.met_context,
          global_connect_country: match.global_connect_country,
          is_global_match: match.is_global_match,
          arena_id: match.arena_id,
          source_context: match.source_context,
          matchedProfile: match.user1_id === user.id ? match.user2 : match.user1,
        }));
        setMatches(transformedMatches);
      }
    } catch (err) {
      console.error("Error fetching unified matches:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch matches"));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Filter matches by source
  const filteredMatches = matches.filter((match) => {
    if (sourceFilter === "all") return true;
    return match.match_source === sourceFilter;
  });

  // Subscribe to new matches
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("unified-matches-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "matches",
          filter: `user1_id=eq.${user.id}`,
        },
        () => fetchMatches()
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "matches",
          filter: `user2_id=eq.${user.id}`,
        },
        () => fetchMatches()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchMatches]);

  // Initial fetch
  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  // Stats
  const stats = {
    total: matches.length,
    exhibition: matches.filter((m) => m.match_source === "exhibition").length,
    popBalloon: matches.filter((m) => m.match_source === "pop_balloon").length,
    globalConnect: matches.filter((m) => m.match_source === "global_connect").length,
  };

  return {
    matches: filteredMatches,
    allMatches: matches,
    loading,
    error,
    sourceFilter,
    setSourceFilter,
    stats,
    refetch: fetchMatches,
  };
};
